import { Component } from '@theme/component';

// Seconds each image stays before fading to the next.
const SLIDE_SECONDS = 7;

/**
 * Slowly crossfades the hero's images on a timer.
 * The first image is visible from the start, so the hero looks complete before this module runs.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} slides - The image wrappers to cycle through, in order.
 *
 * @extends Component<Refs>
 */
class HeroSlideshow extends Component {
  requiredRefs = ['slides'];

  /** @type {ReturnType<typeof setInterval> | undefined} */
  #timer;

  #index = 0;

  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    super.connectedCallback();
    this.#reducedMotion.addEventListener('change', this.#start);
    this.#start();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#reducedMotion.removeEventListener('change', this.#start);
    clearInterval(this.#timer);
  }

  #start = () => {
    clearInterval(this.#timer);
    if (this.#reducedMotion.matches || this.refs.slides.length < 2) return;

    this.#timer = setInterval(this.#next, SLIDE_SECONDS * 1000);
  };

  #next = () => {
    const { slides } = this.refs;

    slides[this.#index]?.removeAttribute('data-active');
    this.#index = (this.#index + 1) % slides.length;
    slides[this.#index]?.setAttribute('data-active', '');
  };
}

if (!customElements.get('hero-slideshow')) {
  customElements.define('hero-slideshow', HeroSlideshow);
}
